import React, { FC, useMemo, useRef, useState } from 'react'
import { Table as ReactBootstrapTable } from 'react-bootstrap'
import cn from 'classnames'

import { TableCaption } from './table-caption'
import { Spinner } from '../spinner'
import { TableHeader } from './table-header'
import TableBody from './table-body/table-body'
import { TablePagination } from './table-pagination'

import { TableProps } from './table-types'

import s from './table.module.scss'

const Table: FC<TableProps & {
  renderExpandableRow?: (row: any) => React.ReactNode
}> = props => {
  const {
    tableInstance,
    isGlobalFilterEnabled = false,
    actions,
    totalItems = 0,
    className = '',
    tableWrapperClassName = '',
    renderAdditionalFilters,
    renderSecondHeader,
    reorderSettings,
    isLoading,
    isError,
    isFlex = true,
    renderExpandableRow,
  } = props

  const tableRef = useRef<HTMLTableElement>(null)
  const { rows } = tableInstance.getRowModel()
  const isTableLoading = useMemo(() => rows.length !== 0 && isLoading, [rows.length, isLoading])
  // Accordion state: only one expanded row at a time
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  return (
    <div className={cn(s['table-container'], className)}>
      <TableCaption
        tableInstance={tableInstance}
        isGlobalFilterEnabled={isGlobalFilterEnabled}
        renderAdditionalFilters={renderAdditionalFilters}
        actions={actions}
        isRowsDraggable={Boolean(reorderSettings?.isRowsDraggable)}
      />

      {typeof renderSecondHeader === 'function' && renderSecondHeader()}

      <div className={cn(s['table-wrapper'], tableWrapperClassName)}>
        {isTableLoading && (
          <div className={cn(s['tr-loader'], { [s['tr-loader-flex']]: isFlex })}>
            <Spinner />
          </div>
        )}

        <ReactBootstrapTable className={cn(s['table'], { [s['table-flex']]: isFlex })} ref={tableRef}>
          <TableHeader
            tableInstance={tableInstance}
            isRowsDraggable={Boolean(reorderSettings?.isRowsDraggable)}
            isFlex={isFlex}
            hasExpandableRows={!!renderExpandableRow}
          />
          <tbody>
            {renderExpandableRow && !(rows.length === 0 && isLoading) && !isError && rows.length > 0
              ? 
              rows.map(row => {
                  const rowId = row.id ?? row.original?.id ?? row.index ?? row.getValue?.('id') ?? Math.random();
                  const isExpanded = expandedRow === rowId;
                  const handleChevronClick = () => {
                    setExpandedRow(isExpanded ? null : rowId);
                  };
                  return (
                    <React.Fragment key={rowId}>
                      <tr>
                        <td key={`carat-${rowId}`} className={s['carat-cell']}>
                          {renderExpandableRow(row.original) && (
                            <button
                              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                              onClick={handleChevronClick}
                              className={cn('bi', 'bi-chevron-down', s['chevron-btn'], isExpanded ? s['chevron-btn-expanded'] : s['chevron-btn-collapsed'])}
                              aria-expanded={isExpanded}
                            >
                            </button>
                          )}
                        </td>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>
                            {typeof cell.column.columnDef.cell === 'function'
                              ? cell.column.columnDef.cell(cell.getContext())
                              : cell.getValue?.()}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td colSpan={row.getVisibleCells().length + 1} className={s['expandable-row-cell']}>
                          <div
                            className={cn(s['expandable-transition'], { [s['expandable-transition--open']]: isExpanded })}
                          >
                            {renderExpandableRow(row.original)}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              : (
                <TableBody
                  tableInstance={tableInstance}
                  reorderSettings={reorderSettings}
                  isLoading={!isTableLoading && isLoading}
                  isError={isError}
                  isFlex={isFlex}
                />
              )
            }
          </tbody>
        </ReactBootstrapTable>
      </div>

      {tableInstance.getPaginationRowModel() && (
        <TablePagination tableInstance={tableInstance} totalItems={totalItems} />
      )}
    </div>
  )
}

export default Table
